import React, { useEffect, useState } from 'react';
import {
  Calendar, Users, CheckSquare, Pill, Activity, Plus, X, Video, Star, Search,
  UserPlus, Clock, ChevronDown, TrendingUp, Leaf, MessageSquare, PlayCircle, CalendarDays
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, LoadingState, StatusBadge, Avatar, DoshaTag, Badge, Button, Modal, Input, Select, EmptyState, ProgressBar, JoinGMeetButton } from '../../components/common/UI';
import { appointmentsAPI, usersAPI, attendanceAPI, prescriptionsAPI, therapiesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/doctor/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/doctor/appointments', label: 'Appointments', icon: Calendar },
  { path: '/doctor/patients', label: 'My Patients', icon: Users },
  { path: '/doctor/attendance', label: 'Attendance', icon: CheckSquare },
  { path: '/doctor/prescriptions', label: 'Prescriptions', icon: Pill },
];

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtShort = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const APPOINTMENT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'panchakarma', label: 'Panchakarma Procedure' },
];

// Common classical Ayurvedic formulary with standard dosage guidance.
// Doctors can still type a custom medicine name if it isn't listed here.
const AYURVEDIC_MEDICINES = [
  { name: 'Triphala Churna', category: 'Churna', dosage: '3-5g', frequency: 'Once daily', timing: 'before-meal' },
  { name: 'Trikatu Churna', category: 'Churna', dosage: '1-3g', frequency: 'Twice daily', timing: 'before-meal' },
  { name: 'Sitopaladi Churna', category: 'Churna', dosage: '3-6g', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Talisadi Churna', category: 'Churna', dosage: '1-3g', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Hingvastak Churna', category: 'Churna', dosage: '3g', frequency: 'Twice daily', timing: 'with-meal' },
  { name: 'Avipattikar Churna', category: 'Churna', dosage: '3-6g', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Ashwagandha Churna', category: 'Churna', dosage: '3-6g', frequency: 'Twice daily', timing: 'with-meal' },
  { name: 'Chyawanprash', category: 'Lehya', dosage: '10-20g', frequency: 'Once daily', timing: 'before-meal' },
  { name: 'Brahma Rasayana', category: 'Lehya', dosage: '10g', frequency: 'Once daily', timing: 'before-meal' },
  { name: 'Triphala Guggulu', category: 'Vati', dosage: '1-2 tablets', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Yograj Guggulu', category: 'Vati', dosage: '1-2 tablets', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Chandraprabha Vati', category: 'Vati', dosage: '1-2 tablets', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Arogyavardhini Vati', category: 'Vati', dosage: '1 tablet', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Gokshuradi Guggulu', category: 'Vati', dosage: '1-2 tablets', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Kutajghan Vati', category: 'Vati', dosage: '1-2 tablets', frequency: 'Twice daily', timing: 'before-meal' },
  { name: 'Shankha Vati', category: 'Vati', dosage: '1 tablet', frequency: 'Twice daily', timing: 'before-meal' },
  { name: 'Dashmool Kwath', category: 'Kwatha', dosage: '15-20ml', frequency: 'Twice daily', timing: 'before-meal' },
  { name: 'Punarnavadi Kwath', category: 'Kwatha', dosage: '15-20ml', frequency: 'Twice daily', timing: 'before-meal' },
  { name: 'Saraswatarishta', category: 'Arishta', dosage: '15-20ml', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Ashokarishta', category: 'Arishta', dosage: '15-20ml', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Kumaryasava', category: 'Arishta', dosage: '15-20ml', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Dashmoolarishta', category: 'Arishta', dosage: '15-20ml', frequency: 'Twice daily', timing: 'after-meal' },
  { name: 'Mahanarayan Taila', category: 'Taila (External)', dosage: 'For local application', frequency: 'Once daily', timing: 'bedtime' },
  { name: 'Ksheerabala Taila', category: 'Taila (External)', dosage: 'For local application', frequency: 'Once daily', timing: 'bedtime' },
];
const MEDICINE_CATEGORIES = [...new Set(AYURVEDIC_MEDICINES.map(m => m.category))];

// ============ APPOINTMENTS ============
export const DoctorAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [tab, setTab] = useState('today');
  const [search, setSearch] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [sessionModal, setSessionModal] = useState(null);
  const [savingSession, setSavingSession] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const emptyScheduleForm = { patient: '', therapy: '', type: 'therapy-session', date: '', startTime: '', endTime: '', notes: '' };
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);

  const emptySessionForm = { proceduresPerformed: '', duration: '', patientResponse: '', effectsObserved: '', nextRecommendation: '', sessionNotes: '' };
  const [sessionForm, setSessionForm] = useState(emptySessionForm);

  useEffect(() => {
    Promise.all([
      appointmentsAPI.getAll(),
      usersAPI.getDoctorPatients(),
      usersAPI.getAll({ role: 'patient' }),
      therapiesAPI.getAll()
    ]).then(([aRes, pRes, allRes, tRes]) => {
      setAppointments(aRes.data.appointments || []);
      const docPatients = pRes.data.patients || [];
      const allPatients = allRes.data.users || [];
      const combined = [...docPatients];
      allPatients.forEach(p => {
        if (!combined.some(cp => cp._id === p._id)) {
          combined.push(p);
        }
      });
      setPatients(combined);
      setTherapies(tRes.data.therapies || []);
    }).catch(() => toast.error('Failed to load appointments'))
      .finally(() => setLoading(false));
  }, []);

  const resetSchedule = () => { setShowSchedule(false); setScheduleForm(emptyScheduleForm); };

  const handleSchedule = async () => {
    if (!scheduleForm.patient || !scheduleForm.date || !scheduleForm.startTime || !scheduleForm.endTime) {
      toast.error('Fill in patient, date and time');
      return;
    }
    setScheduling(true);
    try {
      const res = await appointmentsAPI.create(scheduleForm);
      setAppointments(prev => [res.data.appointment, ...prev]);
      toast.success('Appointment scheduled!');
      resetSchedule();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to schedule appointment');
    } finally { setScheduling(false); }
  };

  const quickUpdate = async (id, status) => {
    setUpdating(id);
    try {
      const res = await appointmentsAPI.update(id, { status });
      setAppointments(prev => prev.map(a => a._id === id ? res.data.appointment : a));
      toast.success(`Marked as ${status}`);
    } catch { toast.error('Failed to update'); }
    finally { setUpdating(null); }
  };

  const openSessionModal = (a) => {
    setSessionModal(a);
    setSessionForm(a.sessionDetails || emptySessionForm);
  };

  const saveSession = async () => {
    if (!sessionForm.proceduresPerformed.trim()) { toast.error('Describe the procedures performed'); return; }
    setSavingSession(true);
    try {
      const res = await appointmentsAPI.update(sessionModal._id, {
        status: 'completed',
        sessionDetails: sessionForm,
        sessionNotes: sessionForm.sessionNotes
      });
      setAppointments(prev => prev.map(a => a._id === sessionModal._id ? res.data.appointment : a));
      toast.success('Session details saved');
      setSessionModal(null);
    } catch { toast.error('Failed to save session'); }
    finally { setSavingSession(false); }
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="doctor"><LoadingState /></DashboardLayout>;

  const now = new Date();
  const isToday = (d) => new Date(d).toDateString() === now.toDateString();
  let filtered = appointments;
  if (tab === 'today') filtered = appointments.filter(a => isToday(a.date));
  else if (tab === 'upcoming') filtered = appointments.filter(a => new Date(a.date) > now && a.status !== 'completed' && a.status !== 'cancelled');
  else if (tab === 'past') filtered = appointments.filter(a => a.status === 'completed' || (new Date(a.date) < now && !isToday(a.date)));
  if (search.trim()) filtered = filtered.filter(a => a.patient?.name?.toLowerCase().includes(search.toLowerCase()));

  const TABS = [
    { key: 'today', label: 'Today' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'past', label: 'Past' },
    { key: 'all', label: 'All' },
  ];

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Appointments</h1>
        <Button onClick={() => setShowSchedule(true)}><Plus className="w-4 h-4" /> Schedule Appointment</Button>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 bg-gray-100 rounded-xl p-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${tab === t.key ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patient..."
            className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
        </div>
      </div>

      <Card>
        {filtered.length === 0 ? (
          <EmptyState icon={Calendar} title="No appointments" description="Nothing here yet" />
        ) : (
          <div className="space-y-3">
            {filtered.map(a => {
              const expanded = expandedId === a._id;
              return (
                <div key={a._id} className="border border-gray-100 rounded-2xl hover:border-blue-200 transition-all overflow-hidden">
                  <div className="flex flex-col sm:flex-row gap-4 p-4">
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar name={a.patient?.name} color="blue" size="md" />
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{a.patient?.name}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                          <Leaf className="w-3 h-3 text-emerald-500" /> {a.therapy?.name || a.type}
                          <span className="text-gray-300">•</span>
                          <Clock className="w-3 h-3" /> {fmt(a.date)} · {a.startTime}–{a.endTime}
                        </p>
                        {a.patient?.patientProfile?.dosha && <DoshaTag dosha={a.patient.patientProfile.dosha} />}
                        {a.notes && <p className="text-xs text-gray-400 mt-1 italic">Note: {a.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <StatusBadge status={a.status} />
                      <JoinGMeetButton appointment={a} />
                      {(a.status === 'scheduled' || a.status === 'confirmed') && (
                        <>
                          <Button size="sm" variant="secondary" loading={updating === a._id} onClick={() => quickUpdate(a._id, 'in-progress')}>In Progress</Button>
                          <Button size="sm" variant="primary" onClick={() => openSessionModal(a)}>Complete</Button>
                        </>
                      )}
                      {a.status === 'in-progress' && <Button size="sm" variant="primary" onClick={() => openSessionModal(a)}>Complete</Button>}
                      {(a.sessionDetails || a.status === 'completed') && (
                        <button onClick={() => setExpandedId(expanded ? null : a._id)} className="text-gray-400 hover:text-blue-500">
                          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                  </div>
                  {expanded && a.sessionDetails && (
                    <div className="bg-gray-50 border-t border-gray-100 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      {a.sessionDetails.proceduresPerformed && (
                        <div><p className="text-gray-400 uppercase font-bold tracking-wider mb-1">Procedures</p><p className="text-gray-700">{a.sessionDetails.proceduresPerformed}</p></div>
                      )}
                      {a.sessionDetails.duration && (
                        <div><p className="text-gray-400 uppercase font-bold tracking-wider mb-1">Duration</p><p className="text-gray-700">{a.sessionDetails.duration}</p></div>
                      )}
                      {a.sessionDetails.patientResponse && (
                        <div><p className="text-gray-400 uppercase font-bold tracking-wider mb-1">Patient Response</p><p className="text-gray-700">{a.sessionDetails.patientResponse}</p></div>
                      )}
                      {a.sessionDetails.effectsObserved && (
                        <div><p className="text-gray-400 uppercase font-bold tracking-wider mb-1">Effects Observed</p><p className="text-gray-700">{a.sessionDetails.effectsObserved}</p></div>
                      )}
                      {a.sessionDetails.nextRecommendation && (
                        <div className="sm:col-span-2"><p className="text-gray-400 uppercase font-bold tracking-wider mb-1">Next Recommendation</p><p className="text-gray-700">{a.sessionDetails.nextRecommendation}</p></div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Schedule Modal */}
      <Modal isOpen={showSchedule} onClose={resetSchedule} title="Schedule Appointment" size="md">
        <div className="space-y-4">
          <Select label="Patient *" value={scheduleForm.patient} onChange={e => setScheduleForm(f => ({ ...f, patient: e.target.value }))}>
            <option value="">Select patient</option>
            {patients.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Select label="Therapy" value={scheduleForm.therapy} onChange={e => setScheduleForm(f => ({ ...f, therapy: e.target.value }))}>
            <option value="">None</option>
            {therapies.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </Select>
          <Select label="Type" value={scheduleForm.type} onChange={e => setScheduleForm(f => ({ ...f, type: e.target.value }))}>
            {APPOINTMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
          <div className="grid grid-cols-3 gap-3">
            <Input label="Date *" type="date" value={scheduleForm.date} onChange={e => setScheduleForm(f => ({ ...f, date: e.target.value }))} />
            <Input label="Start Time *" type="time" value={scheduleForm.startTime} onChange={e => setScheduleForm(f => ({ ...f, startTime: e.target.value }))} />
            <Input label="End Time *" type="time" value={scheduleForm.endTime} onChange={e => setScheduleForm(f => ({ ...f, endTime: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea rows={2} value={scheduleForm.notes} onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={resetSchedule}>Cancel</Button>
            <Button onClick={handleSchedule} loading={scheduling}>Schedule</Button>
          </div>
        </div>
      </Modal>

      {/* Session Details Modal */}
      <Modal isOpen={!!sessionModal} onClose={() => setSessionModal(null)} title={`Session Details — ${sessionModal?.patient?.name || ''}`} size="md">
        {sessionModal && (
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700 flex items-center gap-2">
              <Leaf className="w-4 h-4" /> {sessionModal.therapy?.name || sessionModal.type} · {fmt(sessionModal.date)}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Procedures Performed *</label>
              <textarea rows={2} value={sessionForm.proceduresPerformed} onChange={e => setSessionForm(f => ({ ...f, proceduresPerformed: e.target.value }))}
                placeholder="e.g., Abhyanga full body massage with Bala oil, followed by Swedana"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Duration" placeholder="e.g., 45 mins" value={sessionForm.duration} onChange={e => setSessionForm(f => ({ ...f, duration: e.target.value }))} />
              <Input label="Patient Response" placeholder="e.g., Relaxed, mild dizziness" value={sessionForm.patientResponse} onChange={e => setSessionForm(f => ({ ...f, patientResponse: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Effects Observed</label>
              <textarea rows={2} value={sessionForm.effectsObserved} onChange={e => setSessionForm(f => ({ ...f, effectsObserved: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Next Session Recommendation</label>
              <textarea rows={2} value={sessionForm.nextRecommendation} onChange={e => setSessionForm(f => ({ ...f, nextRecommendation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">General Notes</label>
              <textarea rows={2} value={sessionForm.sessionNotes} onChange={e => setSessionForm(f => ({ ...f, sessionNotes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setSessionModal(null)}>Cancel</Button>
              <Button onClick={saveSession} loading={savingSession}>Save &amp; Complete</Button>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

// ============ PATIENTS ============
export const DoctorPatients = () => {
  const [patients, setPatients] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('overview');
  const [updating, setUpdating] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [showAssign, setShowAssign] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [therapyForm, setTherapyForm] = useState({ therapy: '', targetSessions: '', notes: '' });
  const [savingTherapy, setSavingTherapy] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});

  useEffect(() => {
    Promise.all([
      usersAPI.getDoctorPatients(),
      usersAPI.getAll({ role: 'patient' }),
      therapiesAPI.getAll()
    ]).then(([pRes, allRes, tRes]) => {
      let pats = pRes.data.patients || [];
      if (pats.length === 0) {
        pats = allRes.data.users || [];
      }
      setPatients(pats);
      setTherapies(tRes.data.therapies || []);
    }).catch(() => toast.error('Failed to load patients'))
      .finally(() => setLoading(false));
  }, []);

  const openAssign = async () => {
    setShowAssign(true);
    try {
      const res = await usersAPI.getUnassignedPatients();
      setUnassigned(res.data.patients || []);
    } catch { setUnassigned([]); }
  };

  const assignPatient = async (patientId) => {
    setAssigning(patientId);
    try {
      const res = await usersAPI.assignPatient(patientId);
      setPatients(prev => [res.data.patient, ...prev]);
      setUnassigned(prev => prev.filter(p => p._id !== patientId));
      toast.success('Patient assigned to you');
    } catch { toast.error('Failed to assign patient'); }
    finally { setAssigning(null); }
  };

  const openPatient = (p) => {
    setSelected(p);
    setTab('overview');
    setProgressVal(p.patientProfile?.therapyProgress || 0);
    setTherapyForm({
      therapy: p.patientProfile?.currentTherapy?._id || p.patientProfile?.currentTherapy || '',
      targetSessions: p.patientProfile?.targetSessions || '',
      notes: p.patientProfile?.therapyNotes || ''
    });
  };

  const updateProgress = async () => {
    setUpdating(true);
    try {
      await usersAPI.update(selected._id, { patientProfile: { ...selected.patientProfile, therapyProgress: progressVal } });
      setPatients(prev => prev.map(p => p._id === selected._id ? { ...p, patientProfile: { ...p.patientProfile, therapyProgress: progressVal } } : p));
      toast.success('Progress updated!');
    } catch { toast.error('Failed to update'); }
    finally { setUpdating(false); }
  };

  const saveTherapyPlan = async () => {
    setSavingTherapy(true);
    try {
      const updatedProfile = {
        ...selected.patientProfile,
        currentTherapy: therapyForm.therapy,
        targetSessions: therapyForm.targetSessions,
        therapyNotes: therapyForm.notes
      };
      await usersAPI.update(selected._id, { patientProfile: updatedProfile });
      setPatients(prev => prev.map(p => p._id === selected._id ? { ...p, patientProfile: updatedProfile } : p));
      setSelected(prev => ({ ...prev, patientProfile: updatedProfile }));
      toast.success('Therapy plan updated');
    } catch { toast.error('Failed to update therapy plan'); }
    finally { setSavingTherapy(false); }
  };

  const saveVideoComment = async (logIndex) => {
    const comment = commentDrafts[logIndex];
    if (!comment?.trim()) return;
    try {
      const logs = [...(selected.patientProfile?.videoCheckins || [])];
      logs[logIndex] = { ...logs[logIndex], doctorComment: comment, reviewed: true };
      const updatedProfile = { ...selected.patientProfile, videoCheckins: logs };
      await usersAPI.update(selected._id, { patientProfile: updatedProfile });
      setSelected(prev => ({ ...prev, patientProfile: updatedProfile }));
      setPatients(prev => prev.map(p => p._id === selected._id ? { ...p, patientProfile: updatedProfile } : p));
      toast.success('Feedback sent to patient');
    } catch { toast.error('Failed to save comment'); }
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="doctor"><LoadingState /></DashboardLayout>;

  const TABS = [
    { key: 'overview', label: 'Overview', icon: Users },
    { key: 'therapy', label: 'Therapy Plan', icon: Leaf },
    { key: 'progress', label: 'Progress & Videos', icon: Video },
    { key: 'feedback', label: 'Feedback', icon: MessageSquare },
  ];

  const videoLogs = selected?.patientProfile?.videoCheckins || [];
  const feedbackList = selected?.patientProfile?.feedback || [];
  const therapyHistory = selected?.patientProfile?.therapyHistory || [];

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">My Patients ({patients.length})</h1>
        <Button onClick={openAssign}><UserPlus className="w-4 h-4" /> Assign Patient</Button>
      </div>

      {patients.length === 0 ? (
        <Card><EmptyState icon={Users} title="No patients assigned" description="Assign patients to start tracking their therapy" action={<Button onClick={openAssign}>Assign Patient</Button>} /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {patients.map(p => (
            <div key={p._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-5 cursor-pointer" onClick={() => openPatient(p)}>
              <div className="flex items-center gap-3 mb-4">
                <Avatar name={p.name} color="blue" size="lg" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-800 truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.email}</p>
                  {p.patientProfile?.dosha && <DoshaTag dosha={p.patientProfile.dosha} />}
                </div>
              </div>
              <div className="space-y-3">
                <ProgressBar value={p.patientProfile?.therapyProgress || 0} label="Therapy Progress" color="blue" />
                {p.patientProfile?.currentTherapy && (
                  <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-1">
                    <Leaf className="w-3 h-3 text-emerald-500" /> Current: <span className="font-medium text-gray-700">{p.patientProfile.currentTherapy?.name || p.patientProfile.currentTherapy}</span>
                  </p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Video className="w-3 h-3" /> {(p.patientProfile?.videoCheckins || []).length} check-ins</span>
                  <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {(p.patientProfile?.feedback || []).length} feedback</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {p.patientProfile?.medicalHistory?.slice(0, 2).map(h => <Badge key={h} color="gray">{h}</Badge>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Patient Modal */}
      <Modal isOpen={showAssign} onClose={() => setShowAssign(false)} title="Assign a Patient" size="md">
        <div className="space-y-3">
          {unassigned.length === 0 ? (
            <EmptyState icon={UserPlus} title="No unassigned patients" description="All patients currently have a doctor assigned" />
          ) : unassigned.map(p => (
            <div key={p._id} className="flex items-center justify-between p-3 border border-gray-100 rounded-xl">
              <div className="flex items-center gap-3">
                <Avatar name={p.name} color="gray" size="md" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.email}</p>
                </div>
              </div>
              <Button size="sm" loading={assigning === p._id} onClick={() => assignPatient(p._id)}>Assign to Me</Button>
            </div>
          ))}
        </div>
      </Modal>

      {/* Patient Detail Modal */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title={selected?.name || ''} size="lg">
        {selected && (
          <div className="space-y-5">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${tab === t.key ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-gray-400 text-xs">Email</p><p className="font-medium">{selected.email}</p></div>
                  <div><p className="text-gray-400 text-xs">Phone</p><p className="font-medium">{selected.phone || '—'}</p></div>
                  <div><p className="text-gray-400 text-xs">Blood Group</p><p className="font-medium">{selected.patientProfile?.bloodGroup || '—'}</p></div>
                  <div><p className="text-gray-400 text-xs">Gender</p><p className="font-medium">{selected.patientProfile?.gender || '—'}</p></div>
                  <div><p className="text-gray-400 text-xs">Dosha</p>{selected.patientProfile?.dosha ? <DoshaTag dosha={selected.patientProfile.dosha} /> : '—'}</div>
                  <div><p className="text-gray-400 text-xs">Address</p><p className="font-medium text-xs">{selected.patientProfile?.address || '—'}</p></div>
                </div>
                {selected.patientProfile?.medicalHistory?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Medical History</p>
                    <div className="flex flex-wrap gap-1">{selected.patientProfile.medicalHistory.map(h => <Badge key={h} color="amber">{h}</Badge>)}</div>
                  </div>
                )}
                {selected.patientProfile?.allergies?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Allergies</p>
                    <div className="flex flex-wrap gap-1">{selected.patientProfile.allergies.map(a => <Badge key={a} color="red">{a}</Badge>)}</div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Overall Therapy Progress</p>
                  <div className="flex items-center gap-3">
                    <input type="range" min="0" max="100" value={progressVal} onChange={e => setProgressVal(Number(e.target.value))} className="flex-1 accent-blue-500" />
                    <span className="text-sm font-bold text-blue-600 w-10 text-right">{progressVal}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                    <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${progressVal}%` }} />
                  </div>
                  <div className="flex justify-end pt-3">
                    <Button onClick={updateProgress} loading={updating} size="sm">Update Progress</Button>
                  </div>
                </div>
              </div>
            )}

            {tab === 'therapy' && (
              <div className="space-y-5">
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1"><Leaf className="w-3.5 h-3.5" /> Current Therapy Plan</p>
                  <Select label="Therapy" value={therapyForm.therapy} onChange={e => setTherapyForm(f => ({ ...f, therapy: e.target.value }))}>
                    <option value="">None assigned</option>
                    {therapies.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                  </Select>
                  <Input label="Target Sessions" type="number" value={therapyForm.targetSessions} onChange={e => setTherapyForm(f => ({ ...f, targetSessions: e.target.value }))} />
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Therapy Notes</label>
                    <textarea rows={2} value={therapyForm.notes} onChange={e => setTherapyForm(f => ({ ...f, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-emerald-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500 bg-white resize-none" />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={saveTherapyPlan} loading={savingTherapy} size="sm" className="bg-emerald-600 hover:bg-emerald-700">Save Therapy Plan</Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Therapy History</p>
                  {therapyHistory.length === 0 ? (
                    <p className="text-xs text-gray-400">No past therapies recorded yet</p>
                  ) : (
                    <div className="space-y-2">
                      {therapyHistory.map((h, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl text-xs">
                          <div>
                            <p className="font-medium text-gray-700">{h.therapy?.name || h.therapy}</p>
                            <p className="text-gray-400">{h.startDate && fmtShort(h.startDate)}{h.endDate && ` – ${fmtShort(h.endDate)}`}</p>
                          </div>
                          {h.notes && <p className="text-gray-500 italic max-w-[50%] text-right">{h.notes}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'progress' && (
              <div className="space-y-4">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Video className="w-3.5 h-3.5 text-blue-500" /> Daily check-in videos submitted by the patient</p>
                {videoLogs.length === 0 ? (
                  <EmptyState icon={Video} title="No check-ins yet" description="Daily progress videos from the patient will appear here" />
                ) : (
                  <div className="space-y-3">
                    {videoLogs.map((log, i) => (
                      <div key={i} className="border border-gray-100 rounded-xl p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                            <PlayCircle className="w-7 h-7 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-800">{fmt(log.date)}</p>
                              {log.reviewed ? <Badge color="emerald">Reviewed</Badge> : <Badge color="amber">Pending Review</Badge>}
                            </div>
                            {log.note && <p className="text-xs text-gray-500 mt-0.5">Patient note: {log.note}</p>}
                            {log.duration && <p className="text-xs text-gray-400">{log.duration}</p>}
                          </div>
                        </div>
                        {log.doctorComment && (
                          <div className="mt-2 bg-blue-50 rounded-lg p-2 text-xs text-blue-700">Your feedback: {log.doctorComment}</div>
                        )}
                        {!log.doctorComment && (
                          <div className="mt-3 flex gap-2">
                            <input value={commentDrafts[i] || ''} onChange={e => setCommentDrafts(d => ({ ...d, [i]: e.target.value }))}
                              placeholder="Add feedback for the patient..."
                              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500" />
                            <Button size="sm" onClick={() => saveVideoComment(i)}>Send</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'feedback' && (
              <div className="space-y-3">
                {feedbackList.length === 0 ? (
                  <EmptyState icon={MessageSquare} title="No feedback yet" description="Patient feedback on therapy sessions will appear here" />
                ) : feedbackList.map((f, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= (f.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400">{f.date && fmtShort(f.date)}</span>
                    </div>
                    {f.comment && <p className="text-sm text-gray-600">{f.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

// ============ ATTENDANCE ============
export const DoctorAttendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ date: '', reason: '' });
  const [requestingLeave, setRequestingLeave] = useState(false);
  const [month] = useState(new Date().getMonth() + 1);
  const [year] = useState(new Date().getFullYear());

  const load = () => {
    attendanceAPI.getAll({ month, year })
      .then(r => setAttendance(r.data.attendance || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markToday = async (status) => {
    setMarking(true);
    try {
      const now = new Date();
      const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      await attendanceAPI.mark({ date: now.toISOString(), status, checkIn: status === 'present' ? timeStr : undefined });
      toast.success(`Marked ${status} for today`);
      load();
    } catch { toast.error('Failed to mark'); }
    finally { setMarking(false); }
  };

  const requestLeave = async () => {
    if (!leaveForm.date) { toast.error('Select a date'); return; }
    setRequestingLeave(true);
    try {
      await attendanceAPI.mark({ date: leaveForm.date, status: 'leave', reason: leaveForm.reason });
      toast.success('Leave requested');
      setShowLeave(false);
      setLeaveForm({ date: '', reason: '' });
      load();
    } catch { toast.error('Failed to request leave'); }
    finally { setRequestingLeave(false); }
  };

  const today = new Date().toDateString();
  const todayRecord = attendance.find(a => new Date(a.date).toDateString() === today);
  const presentDays = attendance.filter(a => a.status === 'present' || a.status === 'half-day').length;
  const absentDays = attendance.filter(a => a.status === 'absent').length;
  const leaveDays = attendance.filter(a => a.status === 'leave').length;

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOffset = new Date(year, month - 1, 1).getDay();
  const statusByDay = {};
  attendance.forEach(a => { statusByDay[new Date(a.date).getDate()] = a.status; });
  const dotColor = { present: 'bg-emerald-500', 'half-day': 'bg-amber-500', leave: 'bg-violet-500', absent: 'bg-red-500' };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="doctor"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Attendance</h1>
        <Button variant="outline" onClick={() => setShowLeave(true)}><CalendarDays className="w-4 h-4" /> Request Leave</Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center">
          <p className="text-3xl font-bold text-emerald-600">{presentDays}</p>
          <p className="text-sm text-emerald-700 font-medium">Present</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{absentDays}</p>
          <p className="text-sm text-red-700 font-medium">Absent</p>
        </div>
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 text-center">
          <p className="text-3xl font-bold text-violet-600">{leaveDays}</p>
          <p className="text-sm text-violet-700 font-medium">Leave</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{attendance.length > 0 ? Math.round((presentDays / attendance.length) * 100) : 0}%</p>
          <p className="text-sm text-blue-700 font-medium">Rate</p>
        </div>
      </div>

      {/* Today's mark */}
      <div className={`rounded-2xl p-5 mb-6 border ${todayRecord ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-bold text-gray-800">Today — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            {todayRecord
              ? <p className="text-sm text-emerald-700 mt-0.5">Status: <strong className="capitalize">{todayRecord.status}</strong>{todayRecord.checkIn && ` • Check-in: ${todayRecord.checkIn}`}{todayRecord.checkOut && ` • Check-out: ${todayRecord.checkOut}`}</p>
              : <p className="text-sm text-amber-700 mt-0.5">Attendance not yet marked</p>}
          </div>
          {!todayRecord && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => markToday('present')} loading={marking} size="sm">✓ Present</Button>
              <Button onClick={() => markToday('half-day')} loading={marking} size="sm" variant="secondary">½ Half Day</Button>
              <Button onClick={() => markToday('absent')} loading={marking} size="sm" variant="danger">✗ Absent</Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card title="This Month's Record">
            {attendance.length === 0 ? <EmptyState icon={CheckSquare} title="No records" description="Attendance records will appear here" /> : (
              <div className="space-y-2">
                {attendance.map(a => (
                  <div key={a._id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${a.status === 'present' ? 'bg-emerald-500' : a.status === 'half-day' ? 'bg-amber-500' : a.status === 'leave' ? 'bg-violet-500' : 'bg-red-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{fmtShort(a.date)} — {new Date(a.date).toLocaleDateString('en-IN', { weekday: 'long' })}</p>
                        {(a.checkIn || a.checkOut) && <p className="text-xs text-gray-400">{a.checkIn && `In: ${a.checkIn}`}{a.checkIn && a.checkOut && ' • '}{a.checkOut && `Out: ${a.checkOut}`}</p>}
                        {a.reason && <p className="text-xs text-gray-400 italic">Reason: {a.reason}</p>}
                      </div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card title="Month at a Glance">
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-gray-400 mb-1">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOffset }).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const status = statusByDay[day];
                return (
                  <div key={day} className="aspect-square flex flex-col items-center justify-center rounded-lg text-[11px] text-gray-600">
                    {day}
                    {status && <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${dotColor[status]}`} />}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Leave Request Modal */}
      <Modal isOpen={showLeave} onClose={() => setShowLeave(false)} title="Request Leave" size="sm">
        <div className="space-y-4">
          <Input label="Date" type="date" value={leaveForm.date} onChange={e => setLeaveForm(f => ({ ...f, date: e.target.value }))} />
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Reason</label>
            <textarea rows={2} value={leaveForm.reason} onChange={e => setLeaveForm(f => ({ ...f, reason: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowLeave(false)}>Cancel</Button>
            <Button onClick={requestLeave} loading={requestingLeave}>Submit</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

// ============ PRESCRIPTIONS ============
export const DoctorPrescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyMedicine = { name: '', category: '', dosage: '', frequency: '', duration: '', timing: 'after-meal', notes: '' };
  const emptyForm = {
    patient: '',
    therapy: '',
    medicines: [{ ...emptyMedicine }],
    dietaryAdvice: '', lifestyleAdvice: '', restrictions: '', followUpDate: '', notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    Promise.all([
      prescriptionsAPI.getAll(),
      usersAPI.getDoctorPatients(),
      usersAPI.getAll({ role: 'patient' }),
      therapiesAPI.getAll()
    ]).then(([pRes, patRes, allRes, thRes]) => {
      setPrescriptions(pRes.data.prescriptions || []);
      const docPats = patRes.data.patients || [];
      const allPats = allRes.data.users || [];
      const combined = [...docPats];
      allPats.forEach(p => {
        if (!combined.some(cp => cp._id === p._id)) {
          combined.push(p);
        }
      });
      setPatients(combined);
      setTherapies(thRes.data.therapies || []);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  const addMedicine = () => setForm(f => ({ ...f, medicines: [...f.medicines, { ...emptyMedicine }] }));
  const removeMedicine = (i) => setForm(f => ({ ...f, medicines: f.medicines.filter((_, idx) => idx !== i) }));
  const updateMed = (i, field, val) => setForm(f => ({ ...f, medicines: f.medicines.map((m, idx) => idx === i ? { ...m, [field]: val } : m) }));

  const pickCatalogMedicine = (i, medName) => {
    const med = AYURVEDIC_MEDICINES.find(m => m.name === medName);
    setForm(f => ({
      ...f,
      medicines: f.medicines.map((m, idx) => idx === i ? {
        ...m,
        name: medName,
        dosage: med ? med.dosage : m.dosage,
        frequency: med ? med.frequency : m.frequency,
        timing: med ? med.timing : m.timing,
        category: med ? med.category : ''
      } : m)
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
  };

  const validate = () => {
    if (!form.patient) {
      toast.error('Select a patient');
      return false;
    }
    const filled = form.medicines.filter(m => m.name.trim());
    if (filled.length === 0) {
      toast.error('Add at least one medicine');
      return false;
    }
    for (const m of filled) {
      if (!m.dosage.trim() || !m.frequency.trim() || !m.duration.trim()) {
        toast.error(`Fill dosage, frequency, and duration for ${m.name}`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        patient: form.patient,
        therapy: form.therapy || undefined,
        medicines: form.medicines.filter(m => m.name.trim()),
        dietaryAdvice: form.dietaryAdvice.split('\n').map(s => s.trim()).filter(Boolean),
        lifestyleAdvice: form.lifestyleAdvice.split('\n').map(s => s.trim()).filter(Boolean),
        restrictions: form.restrictions.split('\n').map(s => s.trim()).filter(Boolean),
        followUpDate: form.followUpDate || undefined,
        notes: form.notes.trim()
      };
      const res = await prescriptionsAPI.create(payload);
      setPrescriptions(prev => [res.data.prescription, ...prev]);
      closeModal();
      toast.success('Prescription created!');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="doctor"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Prescriptions</h1>
        <Button onClick={() => setShowModal(true)}><Plus className="w-4 h-4" /> New Prescription</Button>
      </div>

      {prescriptions.length === 0 ? (
        <Card><EmptyState icon={Pill} title="No prescriptions" description="Create prescriptions for your patients" action={<Button onClick={() => setShowModal(true)}>Create First Prescription</Button>} /></Card>
      ) : (
        <div className="space-y-4">
          {prescriptions.map(p => (
            <Card key={p._id} title={`Prescription for ${p.patient?.name}`} subtitle={`Created ${fmt(p.createdAt)}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Medicines ({p.medicines?.length})</p>
                  <div className="space-y-2">
                    {p.medicines?.map((m, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-emerald-50 rounded-lg">
                        <Leaf className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                            {m.name} {m.category && <Badge color="emerald">{m.category}</Badge>}
                          </p>
                          <p className="text-xs text-gray-500">{m.dosage} • {m.frequency} • {m.duration}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  {p.dietaryAdvice?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Diet</p>
                      <div className="flex flex-wrap gap-1">{p.dietaryAdvice.slice(0, 3).map((d, i) => <Badge key={i} color="emerald">{d}</Badge>)}</div>
                    </div>
                  )}
                  {p.restrictions?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Restrictions</p>
                      <div className="flex flex-wrap gap-1">{p.restrictions.slice(0, 3).map((r, i) => <Badge key={i} color="red">{r}</Badge>)}</div>
                    </div>
                  )}
                  {p.followUpDate && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1">📅 Follow-up: {fmt(p.followUpDate)}</p>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Prescription Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title="New Prescription" size="lg">
        <div className="space-y-5">
          <Select label="Patient *" value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} disabled={patients.length === 0}>
            <option value="">{patients.length === 0 ? 'No patients yet' : 'Select patient'}</option>
            {patients.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>

          {therapies.length > 0 && (
            <Select label="Related Therapy (optional)" value={form.therapy} onChange={e => setForm(f => ({ ...f, therapy: e.target.value }))}>
              <option value="">None</option>
              {therapies.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </Select>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Medicines *</p>
              <button onClick={addMedicine} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 mb-3 text-xs text-emerald-700 flex items-center gap-2">
              <Leaf className="w-3.5 h-3.5 flex-shrink-0" /> Pick from 24+ classical Ayurvedic formulations below — dosage, frequency and timing fill in automatically and stay fully editable.
            </div>

            <div className="space-y-3">
              {form.medicines.map((med, i) => (
                <div key={i} className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-xl p-4 space-y-3 relative">
                  {i > 0 && <button onClick={() => removeMedicine(i)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>}
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-emerald-500" />
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Medicine {i + 1}</p>
                    {med.category && <Badge color="emerald">{med.category}</Badge>}
                  </div>

                  <Select value={AYURVEDIC_MEDICINES.some(m => m.name === med.name) ? med.name : ''} onChange={e => pickCatalogMedicine(i, e.target.value)}>
                    <option value="">Choose from Ayurvedic formulary...</option>
                    {MEDICINE_CATEGORIES.map(cat => (
                      <optgroup key={cat} label={cat}>
                        {AYURVEDIC_MEDICINES.filter(m => m.category === cat).map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
                      </optgroup>
                    ))}
                  </Select>

                  <Input placeholder="Or type a custom medicine name *" value={med.name} onChange={e => updateMed(i, 'name', e.target.value)} />

                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Dosage (e.g., 3-5g)" value={med.dosage} onChange={e => updateMed(i, 'dosage', e.target.value)} />
                    <Input placeholder="Frequency (Twice daily)" value={med.frequency} onChange={e => updateMed(i, 'frequency', e.target.value)} />
                    <Input placeholder="Duration (30 days)" value={med.duration} onChange={e => updateMed(i, 'duration', e.target.value)} />
                    <Select value={med.timing} onChange={e => updateMed(i, 'timing', e.target.value)}>
                      <option value="before-meal">Before Meal</option>
                      <option value="after-meal">After Meal</option>
                      <option value="with-meal">With Meal</option>
                      <option value="bedtime">Bedtime</option>
                    </Select>
                  </div>
                  <Input placeholder="Notes (optional)" value={med.notes} onChange={e => updateMed(i, 'notes', e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Dietary Advice (one per line)</label>
              <textarea rows={3} value={form.dietaryAdvice} onChange={e => setForm(f => ({ ...f, dietaryAdvice: e.target.value }))} placeholder="Warm foods only&#10;No cold drinks" className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Restrictions (one per line)</label>
              <textarea rows={3} value={form.restrictions} onChange={e => setForm(f => ({ ...f, restrictions: e.target.value }))} placeholder="No caffeine&#10;Avoid spicy food" className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Follow-up Date" type="date" value={form.followUpDate} onChange={e => setForm(f => ({ ...f, followUpDate: e.target.value }))} />
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 resize-none" />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSave} loading={saving} className="bg-blue-600 hover:bg-blue-700">Save Prescription</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default DoctorPatients;